import { Component, HostBinding, computed, input, signal } from '@angular/core';
import { getMimeTypeByFileName } from '@umss/shared/utils';
import { UmssIconComponent } from '../icon/icon.component';

/**
 * Square document preview tile: renders a pre-signed thumbnail image when one is
 * available, and falls back to a file-type icon (PDF / image / generic) when the
 * thumbnail is absent or fails to load.
 *
 * Presentational only — the parent supplies the file name and thumbnail URL.
 */
@Component({
  selector: 'umss-document-thumbnail',
  standalone: true,
  imports: [UmssIconComponent],
  templateUrl: './document-thumbnail.component.html',
  styleUrls: ['./document-thumbnail.component.scss']
})
export class UmssDocumentThumbnailComponent {
  @HostBinding('style.display') display = 'contents';

  /** File name — used for the image alt text and to derive the fallback icon. */
  fileName = input.required<string>();

  /**
   * Pre-signed URL to a thumbnail image. Null/absent when no thumbnail exists
   * (not generated yet, generation failed, or local storage) — the file-type
   * icon is shown instead.
   */
  thumbnailUrl = input<string | null | undefined>(null);

  /** Container width and height. */
  size = input('56px');

  /** Fallback icon size. */
  iconSize = input('32px');

  /**
   * URL that failed to load, if any. Keying the failure to the URL (rather than
   * a boolean) means a fresh pre-signed URL from a re-fetch is retried
   * automatically instead of staying stuck on the icon fallback.
   */
  private failedUrl = signal<string | null | undefined>(null);

  /** Whether a real thumbnail image should be shown (exists and hasn't failed). */
  hasThumbnail = computed(() => {
    const url = this.thumbnailUrl();
    return !!url && this.failedUrl() !== url;
  });

  /** Phosphor icon name to show when there is no usable thumbnail. */
  fallbackIcon = computed(() => {
    const mimeType = getMimeTypeByFileName(this.fileName());
    if (mimeType === 'application/pdf') return 'phosphorFilePdf';
    if (mimeType?.startsWith('image/')) return 'phosphorImage';
    return 'phosphorFileText';
  });

  /** Falls back to the file-type icon when the thumbnail image fails to load. */
  onError(): void {
    this.failedUrl.set(this.thumbnailUrl());
  }
}
